import { faArrowRotateLeft, faFilter } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Stack } from '@mui/material'
import { getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table'
import React, { useEffect, useMemo, useState, useRef } from 'react'
import AbiStatementViewApiUrls from 'src/apiUrls/AbiStatementViewApiUrls'
import { createDefaultValues } from 'src/components/era-package/forms/hooks/useFormConfig'
import RenderFormSection from 'src/components/era-package/forms/RenderFormSection'
import BlankCard from 'src/components/era-package/shared/BlankCard'
import { Table } from 'src/components/era-package/Table'
import { dateRangeFilter, globalFilterFn } from 'src/utils/era-package/filters'
import { StatementDialog } from 'src/views/abi/StatementPage/dialogs'
import { monthlyStatementFilters, monthlyStmtCols, optionsMap, STATEMENT_TYPE } from '../shared'

// INTEGRATION: Fetch data from backend
const fetchData = (setData = () => { }, setLoading = () => { }) => {
  let isAlive = true
  setLoading(true)
  fetch(AbiStatementViewApiUrls.GET_MONTHLY, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
    },
  })
    .then(res => res.json())
    .then(async (result) => {
      if (isAlive && result.status === 200) setData(result.data)
    }).catch(() => {
      if (isAlive) setData([])
    }).finally(() => {
      if (isAlive) setLoading(false)
    })

  return () => { isAlive = false }
}
// END INTEGRATION

const DIALOG_PROPS_RESET = {
  open: false, data: {},
  type: STATEMENT_TYPE.monthly
}

const DEFAULT_FILTERS = createDefaultValues(monthlyStatementFilters)

// Exclude these fields from being auto-mapped onto the table column filter function.
const EXCLUDE_FILTERS = ['global']

export const MonthlyTab = () => {
  const filtersForm = useMemo(() => ({
    fields: monthlyStatementFilters,
    title: 'Filters',
    icon: <FontAwesomeIcon icon={faFilter} />,
  }), [])
  const [filters, setFilters] = useState(DEFAULT_FILTERS)
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [dialogProps, setDialogProps] = useState(DIALOG_PROPS_RESET)

  const table = useReactTable({
    data, columns: monthlyStmtCols,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowCanExpand: row => row.original.relatedEntrySummary?.length > 0,
    getSubRows: row => row.relatedEntrySummary,
    getRowId: row => row.statementNumber,
    globalFilterFn, filterFromLeafRows: true,
    filterFns: {
      dateRange: dateRangeFilter
    },
    initialState: { pagination: { pageSize: 10 } },
    enableSorting: true,
    enableRowSelection: false,
    enableGlobalFilter: true,
  })

  // Initialize table data
  useEffect(() => fetchData(setData, setLoading), [])

  // Filters
  useEffect(() => {
    table.setGlobalFilter(filters.global)
    // flip value for isFinalized toggle since the labels were reversed
    // Assign to separate const to prevent toggle upon navigating away and back or when form is filled
    const correctedToggleFilters = {
      ...filters,
      isFinalized: typeof filters.isFinalized === 'boolean'
        ? !filters.isFinalized // flip if needed
        : filters.isFinalized
    };
    
    const tableFilterState = Object.entries(correctedToggleFilters)
      .map(([id, value]) => ({ id, value }))
      .filter(x => !(EXCLUDE_FILTERS.includes(x.id) || x.id === 'undefined')) // remove non-column filters

    table.setColumnFilters(tableFilterState)
  }, [table, filters])

  const handleRowClick = (row) => {
    setDialogProps({
      open: true,
      type: STATEMENT_TYPE.monthly,
      statementNumber: row.original.pmsNumber,
      finalized: row.original.isFinalized
    })
  }

  const handleReset = () => {
    DEFAULT_FILTERS.isFinalized = true
    setFilters(DEFAULT_FILTERS)
  }

  return <Stack spacing={2}>
    <BlankCard>
      <RenderFormSection section={filtersForm}
        formData={filters} setFormData={setFilters}
        optionsMap={optionsMap} dense spacing={1.5}
        actions={<Button onClick={handleReset}
          sx={{ minWidth: 120, px: 3, py: 1 }}
          startIcon={<FontAwesomeIcon icon={faArrowRotateLeft} />}
        >
          Reset Filters
        </Button>}
      />

    </BlankCard>
    <BlankCard>
      <Table table={table} hover
        rowCount={data.length}
        onRowClick={handleRowClick}
        onRefresh={() => fetchData(setData, setLoading)}
        loading={loading}
      />
    </BlankCard>

    <StatementDialog
      onClose={() => setDialogProps(DIALOG_PROPS_RESET)}
      {...dialogProps}
    />
  </Stack>
}